import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, User, LogOut, Users, MessageCircle, Store, Search, Crown, HelpCircle, CreditCard } from 'lucide-react';
import { navigationItems } from '../mock/data';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import NotificationBell from './NotificationBell';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
  };

  const handleProfileClick = () => {
    navigate('/profile/edit');
  };

  // Check if user is admin (strict admin check)
  const isAdmin = user && (
    (user.email && user.email.toLowerCase().includes('admin')) || 
    (user.role && user.role.toLowerCase() === 'admin') ||
    (user.is_admin === true)
  );

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                src="/assets/tribal-root-logo.png" 
                alt="TribalRoot.AI Logo" 
                className="h-10 w-auto"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <span className="text-xl font-semibold text-gray-800 hidden">TribalRoot.AI</span>
            </Link>
          </div>

          {/* Desktop Navigation - Only show when authenticated */}
          {isAuthenticated && (
            <nav className="hidden md:flex space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          )}

          {/* Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Notification Bell */}
                <NotificationBell />
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>{user?.full_name || 'User'}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 bg-white">
                    <DropdownMenuItem onClick={handleProfileClick}>
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/profile/edit?tab=inbox" 
                        className="flex items-center w-full"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/profile/edit?tab=inbox');
                        }}
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Messages
                      </Link>
                    </DropdownMenuItem>
                    {/* Admin Portal - Only show for admin users */}
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link 
                          to="/admin-portal" 
                          className="flex items-center w-full"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate('/admin-portal');
                          }}
                        >
                          <Crown className="h-4 w-4 mr-2" />
                          Admin Portal
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {/* Marketplace and Tribal Wall removed per user request */}
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/explore" 
                        className="flex items-center w-full"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/explore');
                        }}
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Explore Community
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/support-ticket" 
                        className="flex items-center w-full"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/support-ticket');
                        }}
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Support Ticket
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/subscription" 
                        className="flex items-center w-full"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/subscription');
                        }}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pricing
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <AuthModal>
                  <Button variant="outline" className="px-6">
                    Login
                  </Button>
                </AuthModal>
                <Button 
                  className="bg-green-700 hover:bg-green-800 text-white px-6"
                  onClick={() => navigate('/register')}
                >
                  Register
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Show for all users */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            {isAuthenticated && navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-600 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-2">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="px-3 py-2 text-sm text-gray-600">
                    Welcome, {user?.full_name}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mb-2" 
                    onClick={() => {
                      handleProfileClick();
                      setIsMenuOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </Button>
                  {/* Admin Portal button for mobile - Only show for admin users */}
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      className="w-full mb-2" 
                      onClick={() => {
                        navigate('/admin-portal');
                        setIsMenuOpen(false);
                      }}
                    >
                      <Crown className="h-4 w-4 mr-2" />
                      Admin Portal
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="w-full mb-2" 
                    onClick={() => {
                      navigate('/explore');
                      setIsMenuOpen(false);
                    }}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Explore Community
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full mb-2" 
                    onClick={() => {
                      navigate('/support-ticket');
                      setIsMenuOpen(false);
                    }}
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Support Ticket
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full mb-2" 
                    onClick={() => {
                      navigate('/subscription');
                      setIsMenuOpen(false);
                    }}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pricing
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 px-3">
                  <AuthModal>
                    <Button variant="outline" className="w-full">
                      Login
                    </Button>
                  </AuthModal>
                  <Button 
                    className="bg-green-700 hover:bg-green-800 text-white w-full"
                    onClick={() => {
                      navigate('/register');
                      setIsMenuOpen(false);
                    }}
                  >
                    Register
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;